import type ProgressBar from '../../console/progressBar.js';
import { ProgressBarSymbol } from '../../console/progressBar.js';
import type DAT from '../../models/dats/dat.js';
import type Options from '../../models/options.js';
import type WriteCandidate from '../../models/writeCandidate.js';
import ArrayUtil from '../../utils/arrayUtil.js';
import Module from '../module.js';

/**
 * Validate candidates for write-ability after all generation and filtering has happened.
 */
export default class CandidateValidator extends Module {
  private readonly options: Options;

  constructor(options: Options, progressBar: ProgressBar) {
    super(progressBar, CandidateValidator.name);
    this.options = options;
  }

  /**
   * Validate the {@link WriteCandidate}s.
   */
  validate(dat: DAT, candidates: WriteCandidate[]): WriteCandidate[] {
    if (!this.options.shouldWrite()) {
      // No need to validate, we're not writing
      return [];
    }

    if (candidates.length === 0) {
      this.prefixedLogger.trace(`${dat.getName()}: no candidates to validate`);
      return [];
    }

    this.prefixedLogger.trace(`${dat.getName()}: validating candidates`);
    this.progressBar.setSymbol(ProgressBarSymbol.CANDIDATE_VALIDATING);
    this.progressBar.resetProgress(candidates.length);

    const conflictedOutputPaths = this.validateUniqueOutputPaths(dat, candidates);
    if (conflictedOutputPaths.length > 0) {
      return conflictedOutputPaths;
    }

    this.prefixedLogger.trace(`${dat.getName()}: done validating candidates`);
    return [];
  }

  private validateUniqueOutputPaths(dat: DAT, candidates: WriteCandidate[]): WriteCandidate[] {
    const outputPathsToCandidates = candidates.reduce((map, candidate) => {
      for (const romWithFiles of candidate.getRomsWithFiles()) {
        const key = romWithFiles.getOutputFile().getFilePath();
        if (map.has(key)) {
          map.get(key)?.push(candidate);
        } else {
          map.set(key, [candidate]);
        }
      }
      return map;
    }, new Map<string, WriteCandidate[]>());

    return [...outputPathsToCandidates]
      .filter(([outputPath, candidates]) => {
        const uniqueCandidates = candidates
          .filter(ArrayUtil.filterUniqueMapped((candidate) => candidate.getGame()))
          .toSorted((a, b) => a.getName().localeCompare(b.getName()));
        if (uniqueCandidates.length <= 1) {
          return false;
        }

        const uniqueInputFiles = uniqueCandidates
          .flatMap((candidate) => candidate.getRomsWithFiles())
          .map((romWithFiles) => romWithFiles.getInputFile())
          .filter(ArrayUtil.filterUniqueMapped((inputFile) => inputFile.toString()));
        if (uniqueInputFiles.length <= 1) {
          // There are multiple WriteCandidates, but all of them have either no ROMs and therefore
          // no ROMWithFiles, or all ROMWithFiles all have the same input file.
          // This can happen with CHDs in particular, where a CHD may be parsed as a few different
          // CHD subtypes (e.g. "raw" + CD-ROM, or GD-ROM .bin/.cue and GD-ROM .gdi/.bin/.raw).
          // This will cause candidates with duplicate input->output files, which CandidateWriter
          // will need to de-duplicate.
          return false;
        }

        let message = `${dat.getName()}: multiple games writing to the same output path: ${outputPath}`;
        for (const candidate of uniqueCandidates) {
          message += `\n  ${candidate.getName()}`;
        }
        this.prefixedLogger.error(message);
        return true;
      })
      .flatMap(([, candidates]) => candidates)
      .reduce(ArrayUtil.reduceUnique(), []);
  }
}
